/* eslint-disable no-restricted-properties */
// Absove: Math.pow

import { module } from "angular";
import { forEach } from "lodash";

import type { IImageData, IMap, IPoint, IViewCoverage } from "App/Modules/Models";
import { StreamSplitType } from "App/Modules/Models";

import type { ICoverageCalculationsService } from "./ICoverageCalculationsService";

interface IPoint3 {
    x: number;
    y: number;
    z: number;
}

export default class CoverageCalculationsService implements ICoverageCalculationsService {
    public static $inject: string[] = [];

    // Do all tricky maths related to map coverage calculations. Pure stateless function (easy to unit-test)
    //   camElevation:  mounting height of the camera, in metric units
    //   hFov:          Horizontal field of view, in radians
    //   vFov:          Vertical field of view, in radians
    //   downtiltRad:   Physical down-tilt of the camera (zero means optical axis is parallel to the ground plane,
    //                  positive means tilted downwards)
    //   azimuthRad:    Camera azimuth, in radians. Zero means looking east (positive map x axis), pi/2 means
    //                  looking south(positive map y-axis)
    // Returns 4 points on the ground plane (in metric units) corresponding to the top-left, top-right, bottom-right
    // and bottom-left corners of the camera image, in that order.
    public doCalculateMapCoverageMath(
        camElevation: number,
        hFovRad: number,
        vFovRad: number,
        downtiltRad: number,
        azimuthRad: number,
    ): IPoint[] {
        // The camera coordinate system is (u,v), u pointing right, v pointing down in the camera image.
        // Let's first ignore the azimuth and use a world coordinate where x is to the east and y to the
        // south(corresponds to pixel coordinate directions in the map image). The camera looks east, i.e along
        // the world x - axis.To get a right - handed system, the z axis points towards the earth. The ground plane
        // is at z = 0, so the camera is mounted at z = -camElevation.

        // The strategy is to assume a nomimal camera plane at unit distance in front of the camera, draw a line
        // between the optical center and each image plane corner, and then compute the intersection between this
        // line and the ground plane.

        // First, the optical center (camera center) is located at:
        const cc = { x: 0, y: 0, z: -camElevation };

        // Unit vector pointing along the optical axis
        const q0 = { x: Math.cos(downtiltRad), y: 0, z: Math.sin(downtiltRad) };

        // Vector pointing from the image plane center to the right image border (along the u-axis)
        const qu = { x: 0, y: Math.tan(0.5 * hFovRad), z: 0 };

        // Vector pointing from the image plane center to the bottom image border (along the v-axis).
        // Ignoring downtilt, this vector is [0, 0, tan(0.5*vFov)]. Rotating with pdtRad in the xz plane gives:
        const qv = {
            x: -Math.sin(downtiltRad) * Math.tan(0.5 * vFovRad),
            y: 0,
            z: Math.cos(downtiltRad) * Math.tan(0.5 * vFovRad),
        };

        // Compute the world coordinates of the four image plane corners (top-left, top-right, etc)
        const imgCornerTL = this.addVectors(cc, q0, -1.0, qu, -1.0, qv);
        const imgCornerTR = this.addVectors(cc, q0, 1.0, qu, -1.0, qv);
        const imgCornerBR = this.addVectors(cc, q0, 1.0, qu, 1.0, qv);
        const imgCornerBL = this.addVectors(cc, q0, -1.0, qu, 1.0, qv);

        // Compute the four intersection points on the ground plane
        const groundPoints = [
            this.groundPlaneIntersection(cc, imgCornerTL),
            this.groundPlaneIntersection(cc, imgCornerTR),
            this.groundPlaneIntersection(cc, imgCornerBR),
            this.groundPlaneIntersection(cc, imgCornerBL),
        ];

        // Now rotate the points in the ground plane according to the azimuth angle
        forEach(groundPoints, (gp) => {
            const x = Math.cos(azimuthRad) * gp.x - Math.sin(azimuthRad) * gp.y;
            const y = Math.sin(azimuthRad) * gp.x + Math.cos(azimuthRad) * gp.y;
            gp.x = x;
            gp.y = y;
        });

        return groundPoints;
    }

    // Input is in pixel coordinates
    public calculateMapCoverage(map: IMap, viewCoverage: IViewCoverage) {
        const [hFovRad, vFovRad] = this.getFieldOfViewsInRadians(viewCoverage);
        const camElevation = viewCoverage.siteEquipment.equipmentElevation;
        const azimuthRad = this.toRadians(viewCoverage.rotationOnMap);

        // In a previous version, we had a lower limit for the downward tilt that was depending.
        // on the field of view. This is now changed to a constant. So, much of this logic could
        // be removed, but let's keep this code for a short while longer, in case we need to re-enable it.
        if (!viewCoverage.minDownwardTilt) {
            viewCoverage.minDownwardTilt = this.getMinDownwardTilt(viewCoverage);
        }

        if (viewCoverage.downwardTilt < viewCoverage.minDownwardTilt) {
            viewCoverage.downwardTilt = viewCoverage.minDownwardTilt;
        }

        const downTiltRad = this.toRadians(viewCoverage.downwardTilt);

        // Upper and lower angles of the entire vertical field of view
        const lowerVfovAngleRad = downTiltRad + 0.5 * vFovRad;
        const upperVfovAngleRad = downTiltRad - 0.5 * vFovRad;

        let mapPoints = [];
        let camCoverageUpperY = 0; // Relative to the active split view part

        // Compute map points
        const UPPER_LIMIT_RAD = this.toRadians(0.5);
        if (upperVfovAngleRad < UPPER_LIMIT_RAD) {
            // The vertical field of view covers part of the sky (above the horizon). Compute the "ground part"
            // of the vertical field of view, restricted to cover only parts of the ground plane (upper-bounded
            // by UPPER_LIMIT_RAD)

            // Vertical field of view of the "ground part" of the vFOV
            const vFovGroundPartRad = lowerVfovAngleRad - UPPER_LIMIT_RAD;

            // Downtilt (center angle) of the ground part of the vFOV
            const downTiltGroundPartRad = lowerVfovAngleRad - 0.5 * vFovGroundPartRad;

            // Compute map points in metric coordinates relative to the camera center, corresponding to the
            // "ground part" of the vertical field of view
            mapPoints = this.doCalculateMapCoverageMath(
                camElevation,
                hFovRad,
                vFovGroundPartRad,
                downTiltGroundPartRad,
                azimuthRad,
            );

            // Now compute where the upper parts of the camera coverage polygons end up in the camera image
            // (the camera image points matching the mapPoints)

            // alpha: Angle between the center of the entire vFOV and the upper part of the "ground part"
            const alpha = 0.5 * vFovRad - (vFovRad - vFovGroundPartRad);

            // dy: Distance between the vertical image center and the upper part of the "ground part"
            // (normalized coordinates)
            const f = 0.5 / Math.tan(0.5 * vFovRad);
            const dy = f * Math.tan(alpha);

            // camCoverageUpperY: Distance in normalized coordinates that the upper part of the camera
            // coverage should be downshifted
            camCoverageUpperY = 0.5 - dy;
        } else {
            // No special treatment of ground part (entire fov is ground)
            // Compute map points in metric coordinates relative to the camera center
            mapPoints = this.doCalculateMapCoverageMath(camElevation, hFovRad, vFovRad, downTiltRad, azimuthRad);
        }

        // Translate to map pixel coordinates
        const mapScalePoints = map.scaleCoordinates;
        const mapScaleMeters = map.scaleValue;
        const scaleDistancePx = Math.sqrt(
            Math.pow(mapScalePoints[0].x - mapScalePoints[1].x, 2) +
                Math.pow(mapScalePoints[0].y - mapScalePoints[1].y, 2),
        );

        const mapPixelsPerMeter = scaleDistancePx / mapScaleMeters;
        const cameraPositionPx = viewCoverage.siteEquipment.positionOnMap;

        forEach(mapPoints, (mp) => {
            mp.x = cameraPositionPx.x + mapPixelsPerMeter * mp.x;
            mp.y = cameraPositionPx.y + mapPixelsPerMeter * mp.y;
        });

        viewCoverage.mapCoveragePolygon = mapPoints;

        // Polygon defining the entire split window
        const streamSplitPolygon = this.getStreamSplitPolygon(viewCoverage.streamSplitType);

        // Compute the polygon defining the ground plane part of the split window
        // by moving the top points down by camCoverageUpperY
        const dyPx = camCoverageUpperY * (streamSplitPolygon[2].y - streamSplitPolygon[0].y);
        viewCoverage.cameraCoveragePolygon = streamSplitPolygon;
        viewCoverage.cameraCoveragePolygon[0].y += dyPx;
        viewCoverage.cameraCoveragePolygon[1].y += dyPx;

        // Later, we probably want to store the stream split polygon in the backend as well, e.g. using
        // something like
        // viewCoverage.streamSplitPolygon = streamSplitPolygon;
        // Currently, we just let the stream split enum control everything, so this is not needed.
    }

    // TODO: Make proper unit test out of this
    public UnitTestDraft() {
        // Simple example, 45 deg vfov camera downtilted 45 degrees. Should put the two
        // closest points at x = 0
        const camElevation = 10;
        let vFovRad = this.toRadians(45);
        let hFovRad = 1.2 * vFovRad;
        let absoluteDownTiltRad = this.toRadians(45 + 22.5);
        const azimuthRad = 0;
        let mapPoints = this.doCalculateMapCoverageMath(
            camElevation,
            hFovRad,
            vFovRad,
            absoluteDownTiltRad,
            azimuthRad,
        );
        // expect that mapPoints[2].x and mapPoints[3].x ~= 0
        // expect that mapPoints[0].x and mapPoints[1] ~= 10

        // 90-degree camera with square image plane looking straight down
        vFovRad = this.toRadians(90);
        hFovRad = this.toRadians(90);
        absoluteDownTiltRad = this.toRadians(90);
        // eslint-disable-next-line @typescript-eslint/no-unused-vars
        mapPoints = this.doCalculateMapCoverageMath(camElevation, hFovRad, vFovRad, absoluteDownTiltRad, azimuthRad);
        // expect that:
        // mapPoints[0] = { 10, -10}
        // mapPoints[1] = { 10,  10}
        // mapPoints[2] = {-10,  10}
        // mapPoints[3] = {-10, -10}
    }

    // Return the minimum downward tilt (in degrees) supported by this view coverage.
    // In previous versions, this was restricted such that all four camera corners are
    // mappable to the ground plane. Now, we set it to a hard-coded value. Could be made
    // configurable in the future, so let's keep this function for now.
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    public getMinDownwardTilt(viewCoverage: IViewCoverage): number {
        return 1;
    }

    // Return the horizontal and vertical field of views in radians, computed based on
    // the configured horizontal field of view, aspect ratio and stream split type.
    // Always use this function for getting the actual field of views, to makesure that
    // clamping and treatment of configured aspect ratio and stream split type is consistent
    // everywhere. Usage example:
    //   const [hFovRad, vFovRad] = coverageCalculationsService.getFieldOfViewsInRadians(viewCoverage);
    public getFieldOfViewsInRadians(viewCoverage: IViewCoverage): number[] {
        const hFovRad = this.toRadians(Math.min(viewCoverage.horizontalFieldOfView, 175));

        // Compute the actual aspect ratio of the current stream split
        // (the aspect ratio stored in viewCoverage is the entire video stream image,
        // while the current split window might only be for a subset of the stream).
        const aspectRatio =
            viewCoverage.aspectRatio * this.getStreamSplitAspectRatioMultiplier(viewCoverage.streamSplitType);

        // Compute the vertical field of view using the hFOV and actual aspect ratio
        const vFovRad = 2 * Math.atan(Math.tan(hFovRad / 2.0) / aspectRatio);
        return [hFovRad, vFovRad];
    }

    public toAbsolute(normalized: IPoint, map: IImageData) {
        if (!normalized) {
            return;
        }

        normalized.x *= 1.0 * map.width;
        normalized.y *= 1.0 * map.height;
    }

    public toNormalized(absolute: IPoint, map: IImageData) {
        if (!absolute) {
            return;
        }

        absolute.x *= 1.0 / map.width;
        absolute.y *= 1.0 / map.height;
    }

    public polygonToNormalized(absolute: IPoint[], map: IImageData) {
        if (!absolute) {
            return;
        }

        forEach(absolute, (abs) => this.toNormalized(abs, map));
    }

    public polygonToAbsolute(normalized: IPoint[], map: IImageData) {
        if (!normalized) {
            return;
        }

        forEach(normalized, (norm) => this.toAbsolute(norm, map));
    }

    public polygonToAbsoluteForResolution(normalized: IPoint[], x: number, y: number) {
        if (!normalized) {
            return;
        }

        forEach(normalized, (norm) => this.toAbsoluteForResolution(norm, x, y));
    }

    public polygonToNormalizedForResolution(absolute: IPoint[], x: number, y: number) {
        if (!absolute) {
            return;
        }

        forEach(absolute, (abs) => this.toNormalizedForResolution(abs, x, y));
    }

    // Get a polygon for masking out stuff from the view that is not included in
    // the current split window
    public getInverseStreamSplitPolygon(splitType: StreamSplitType): IPoint[] {
        switch (splitType) {
            case StreamSplitType.NoSplit:
                return [];
            case StreamSplitType.TopHorisontalSplit:
                return [
                    { x: 0, y: 0.5 },
                    { x: 1, y: 0.5 },
                    { x: 1, y: 1 },
                    { x: 0, y: 1 },
                ];
            case StreamSplitType.BottomHorisontalSplit:
                return [
                    { x: 0, y: 0 },
                    { x: 1, y: 0 },
                    { x: 1, y: 0.5 },
                    { x: 0, y: 0.5 },
                ];
            case StreamSplitType.LeftVerticalSplit:
                return [
                    { x: 0.5, y: 0 },
                    { x: 1.0, y: 0 },
                    { x: 1.0, y: 1 },
                    { x: 0.5, y: 1 },
                ];
            case StreamSplitType.RightVerticalSplit:
                return [
                    { x: 0, y: 0 },
                    { x: 0.5, y: 0 },
                    { x: 0.5, y: 1 },
                    { x: 0, y: 1 },
                ];
            case StreamSplitType.TopLeftSplit:
                return [
                    { x: 0.5, y: 0 },
                    { x: 1, y: 0 },
                    { x: 1, y: 1 },
                    { x: 0, y: 1 },
                    { x: 0, y: 0.5 },
                    { x: 0.5, y: 0.5 },
                ];
            case StreamSplitType.TopRightSplit:
                return [
                    { x: 0, y: 0 },
                    { x: 0.5, y: 0 },
                    { x: 0.5, y: 0.5 },
                    { x: 1, y: 0.5 },
                    { x: 1, y: 1 },
                    { x: 0, y: 1 },
                ];
            case StreamSplitType.BottomLeftSplit:
                return [
                    { x: 0, y: 0 },
                    { x: 1, y: 0 },
                    { x: 1, y: 1 },
                    { x: 0.5, y: 1 },
                    { x: 0.5, y: 0.5 },
                    { x: 0, y: 0.5 },
                ];
            case StreamSplitType.BottomRightSplit:
                return [
                    { x: 0, y: 0 },
                    { x: 1, y: 0 },
                    { x: 1, y: 0.5 },
                    { x: 0.5, y: 0.5 },
                    { x: 0.5, y: 1 },
                    { x: 0, y: 1 },
                ];
            default:
                // TODO: Invalid enum value, handle how?
                return [];
        }
    }

    // Compute and return a rectangle containing the stream split window
    // corresponding to the input enum
    public getStreamSplitPolygon(splitType: StreamSplitType): IPoint[] {
        let x0 = 0;
        let y0 = 0;
        let x1 = 0;
        let y1 = 0;

        switch (splitType as any) {
            case StreamSplitType.NoSplit:
                [x0, y0, x1, y1] = [0, 0, 1, 1];
                break;
            case StreamSplitType.TopHorisontalSplit:
                [x0, y0, x1, y1] = [0, 0, 1, 0.5];
                break;
            case StreamSplitType.BottomHorisontalSplit:
                [x0, y0, x1, y1] = [0, 0.5, 1, 1];
                break;
            case StreamSplitType.LeftVerticalSplit:
                [x0, y0, x1, y1] = [0, 0, 0.5, 1];
                break;
            case StreamSplitType.RightVerticalSplit:
                [x0, y0, x1, y1] = [0.5, 0, 1, 1];
                break;
            case StreamSplitType.TopLeftSplit:
                [x0, y0, x1, y1] = [0, 0, 0.5, 0.5];
                break;
            case StreamSplitType.TopRightSplit:
                [x0, y0, x1, y1] = [0.5, 0, 1, 0.5];
                break;
            case StreamSplitType.BottomLeftSplit:
                [x0, y0, x1, y1] = [0, 0.5, 0.5, 1];
                break;
            case StreamSplitType.BottomRightSplit:
                [x0, y0, x1, y1] = [0.5, 0.5, 1, 1];
                break;
            default:
                // TODO: Invalid enum value, log error?
                [x0, y0, x1, y1] = [0, 0, 1, 1];
        }

        // Set a polgon with the four corners of the stream split window
        // (corners in the order top-left, top-right, bottom-right, bottom-left)
        const streamSplitPolygon = [
            { x: x0, y: y0 },
            { x: x1, y: y0 },
            { x: x1, y: y1 },
            { x: x0, y: y1 },
        ];

        return streamSplitPolygon;
    }

    // Return a number telling us how the aspect ratio is changed by a stream split (e.g.
    // a horizontal split of a 16:9 camera actually gives a 32:9 image in the current split window)
    public getStreamSplitAspectRatioMultiplier(splitType: StreamSplitType): number {
        switch (splitType as any) {
            case StreamSplitType.NoSplit:
            case StreamSplitType.TopLeftSplit:
            case StreamSplitType.TopRightSplit:
            case StreamSplitType.BottomLeftSplit:
            case StreamSplitType.BottomRightSplit:
                return 1;
            case StreamSplitType.TopHorisontalSplit:
            case StreamSplitType.BottomHorisontalSplit:
                return 2;
            case StreamSplitType.LeftVerticalSplit:
            case StreamSplitType.RightVerticalSplit:
                return 0.5;
            default:
                // TODO: Invalid enum value, log error?
                return 1;
        }
    }

    // Compute the intersection between the line (p1, p2) and the ground plane (z=0)
    // and return as a 2D point (defining the x,y coordinates in the ground plane)
    private groundPlaneIntersection(p1: IPoint3, p2: IPoint3): IPoint {
        const k = p1.z / (p2.z - p1.z);
        const output = {
            x: p1.x - k * (p2.x - p1.x),
            y: p1.y - k * (p2.y - p1.y),
        };
        return output;
    }

    // Compute va + vb + kc*vc + kd*vd
    // where the v's are 3-vectors and the k's are scalars
    private addVectors(va: IPoint3, vb: IPoint3, kc: number, vc: IPoint3, kd: number, vd: IPoint3): IPoint3 {
        const result = {
            x: va.x + vb.x + kc * vc.x + kd * vd.x,
            y: va.y + vb.y + kc * vc.y + kd * vd.y,
            z: va.z + vb.z + kc * vc.z + kd * vd.z,
        };
        return result;
    }

    private toAbsoluteForResolution(normalized: IPoint, x: number, y: number) {
        if (!normalized) {
            return;
        }

        normalized.x *= 1.0 * x;
        normalized.y *= 1.0 * y;
    }

    private toNormalizedForResolution(absolute: IPoint, x: number, y: number) {
        if (!absolute) {
            return;
        }

        absolute.x *= 1.0 / x;
        absolute.y *= 1.0 / y;
    }

    private toRadians(degrees: number) {
        return (degrees * Math.PI) / 180.0;
    }
}

module("mmGui").service("mmCoverageCalculationsService", CoverageCalculationsService);

// eslint-enable
